/**
 * RadarChart Component
 * Spider/Radar chart for comparing multiple KPIs across assets
 */

import Plot from 'react-plotly.js'
import { Data, Layout } from 'plotly.js'

interface RadarDataSeries {
  name: string
  values: number[]
  color?: string
}

interface RadarChartProps {
  data: RadarDataSeries[]
  categories: string[]
  title?: string
  height?: number
  showLegend?: boolean
  fillOpacity?: number
  className?: string
}

// Default color palette for multiple assets
const DEFAULT_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
  '#06b6d4', // cyan
  '#ec4899', // pink
  '#84cc16', // lime
]

// Convert hex color to rgba
const hexToRgba = (hex: string, alpha: number): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (result) {
    const r = parseInt(result[1], 16)
    const g = parseInt(result[2], 16)
    const b = parseInt(result[3], 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }
  return hex
}

export default function RadarChart({
  data,
  categories,
  title,
  height = 350,
  showLegend = true,
  fillOpacity = 0.2,
  className
}: RadarChartProps) {
  // Guard against empty data
  if (!data || data.length === 0 || !categories || categories.length === 0) {
    return (
      <div className={className}>
        <div className="flex items-center justify-center h-64 text-surface-400">
          No data available for radar chart
        </div>
      </div>
    )
  }

  const traces: Data[] = data.map((series, index) => {
    const color = series.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]
    const fillColor = hexToRgba(color, fillOpacity)

    return {
      type: 'scatterpolar',
      r: [...series.values, series.values[0]], // Close the polygon
      theta: [...categories, categories[0]], // Close the polygon
      name: series.name,
      fill: 'toself',
      fillcolor: fillColor,
      line: {
        color: color,
        width: 2
      },
      marker: {
        size: 6,
        color: color
      },
      hovertemplate: `<b>%{theta}</b><br>%{r:.1f}<extra>${series.name}</extra>`
    } as Data
  })

  const layout: Partial<Layout> = {
    title: title ? {
      text: title,
      font: { color: '#e2e8f0', size: 14 }
    } : undefined,
    height,
    margin: { t: title ? 60 : 40, r: 40, b: 40, l: 40 },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: { color: '#94a3b8', family: 'Inter, sans-serif' },
    polar: {
      bgcolor: 'transparent',
      radialaxis: {
        visible: true,
        range: [0, 100],
        tickfont: { size: 10, color: '#64748b' },
        gridcolor: '#334155',
        linecolor: '#475569',
        tickangle: 0,
        dtick: 25
      },
      angularaxis: {
        tickfont: { size: 11, color: '#94a3b8' },
        gridcolor: '#334155',
        linecolor: '#475569',
        rotation: 90,
        direction: 'clockwise'
      }
    },
    showlegend: showLegend,
    legend: {
      orientation: 'h',
      yanchor: 'bottom',
      y: -0.15,
      xanchor: 'center',
      x: 0.5,
      font: { size: 11 },
      bgcolor: 'transparent'
    }
  }

  const config = {
    displayModeBar: false,
    responsive: true
  }

  return (
    <div className={className}>
      <Plot
        data={traces}
        layout={layout}
        config={config}
        style={{ width: '100%' }}
      />
    </div>
  )
}
